import angular from 'angular';
import '../shared.module';
import { httpFailure } from '../helpers/http-error.helper';

export const UPLOAD_ERROR_CODES = {
  EMPTY: 'FILE_EMPTY',
  TOO_LARGE: 'FILE_TOO_LARGE',
  TYPE: 'FILE_TYPE_NOT_ALLOWED'
};

export function formatBytes(bytes) {
  if (bytes >= 1048576) return `${Math.round((bytes / 1048576) * 10) / 10} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} bytes`;
}

export function acceptList(accept) {
  if (Array.isArray(accept)) return accept.map((rule) => rule.toLowerCase());
  return (accept || '')
    .split(',')
    .map((rule) => rule.trim().toLowerCase())
    .filter(Boolean);
}

export function matchesAccept(mimeType, accept) {
  const rules = acceptList(accept);
  if (!rules.length) return true;
  const type = (mimeType || '').toLowerCase();
  return rules.some((rule) =>
    rule.endsWith('/*') ? type.startsWith(rule.slice(0, -1)) : rule === type
  );
}

export function validateFile(file, { maxBytes, accept } = {}) {
  if (!file || !file.size) {
    return { code: UPLOAD_ERROR_CODES.EMPTY, message: 'Selecione um arquivo com conteúdo.' };
  }
  if (maxBytes && file.size > maxBytes) {
    return {
      code: UPLOAD_ERROR_CODES.TOO_LARGE,
      message: `O arquivo excede o limite de ${formatBytes(maxBytes)}.`
    };
  }
  if (!matchesAccept(file.type, accept)) {
    return { code: UPLOAD_ERROR_CODES.TYPE, message: 'Tipo de arquivo não permitido.' };
  }
  return null;
}

class UploadService {
  constructor($http, $q, $window, API_BASE_URL, DownloadService) {
    this.$http = $http;
    this.$q = $q;
    this.$window = $window;
    this.API_BASE_URL = API_BASE_URL;
    this.DownloadService = DownloadService;
    this.imageCache = new Map();
  }

  validate(file, rules) {
    return validateFile(file, rules);
  }

  upload(file, rules = {}, onProgress) {
    const failure = validateFile(file, rules);
    if (failure) return this.$q.reject(failure);
    const form = new FormData();
    form.append('file', file, file.name);
    return this.$http
      .post(`${this.API_BASE_URL}/files`, form, {
        transformRequest: angular.identity,
        headers: { 'Content-Type': undefined },
        uploadEventHandlers: onProgress
          ? {
              progress: (event) => {
                if (event.lengthComputable)
                  onProgress(Math.round((event.loaded / event.total) * 100));
              }
            }
          : undefined
      })
      .then(
        (response) => response.data,
        (response) => this.$q.reject(httpFailure(response, 'Falha ao enviar o arquivo.'))
      );
  }

  contentUrl(fileId) {
    return `${this.API_BASE_URL}/files/${fileId}/content`;
  }

  preview(fileId) {
    return this.DownloadService.objectUrl(this.contentUrl(fileId));
  }

  cachedFileUrl(fileId) {
    if (!this.imageCache.has(fileId)) {
      const request = this.$http
        .get(this.contentUrl(fileId), { responseType: 'blob', silentErrors: true })
        .then(
          (response) => this.$window.URL.createObjectURL(response.data),
          (error) => {
            this.imageCache.delete(fileId);
            return this.$q.reject(error);
          }
        );
      this.imageCache.set(fileId, request);
    }
    return this.imageCache.get(fileId);
  }
}

UploadService.$inject = ['$http', '$q', '$window', 'API_BASE_URL', 'DownloadService'];

angular.module('app.shared').service('UploadService', UploadService);

export default UploadService;
